import { DatePicker, Radio, Tabs, Tooltip } from '@maxtropy/components';
import { DatePickerProps, RadioChangeEvent, Spin } from 'antd';
import React, { FC, Key, useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import {
  apiV2StationSignboardNodeDetailIndicatorChartValuePost,
  apiV2StationSignboardNodeDetailIndicatorListPost,
  apiV2StationSignboardNodeDetailIndicatorValuePost,
} from '@maxtropy/device-customer-apis-v2';
import { DatePickerType, NodeType, datePickerTypeDisplay } from '@/pages/MonitorDeviceDashBoard/type';
import dayjs, { Dayjs } from 'dayjs';
import Charts from '@/pages/MonitorDeviceDashBoard/components/Charts';
import styles from './index.module.scss';
import { isNil } from 'lodash-es';
import { SingleCurrentItemType } from './type';
import { getDefaultTime } from '@/pages/MonitorDeviceDashBoard/utils';
import { SingleIndicatorBtns } from './config';
import { useLocation } from 'react-router-dom';

const { RangePicker } = DatePicker;

export interface ChartsDataReq {
  indicatorId?: Key; //指标id
  granularityType?: DatePickerType; //时间颗粒度
  startDate?: string; //开始时间
  endDate?: string; //结束时间
}

interface ISingleDeviceContent {
  nodeType?: string;
  nodeId?: string;
}

const SingleDeviceContent: FC<ISingleDeviceContent> = props => {
  const { nodeType, nodeId } = props;
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);

  let url_stationId = urlSearchParams.get('stationId') || undefined;
  let url_stationType = urlSearchParams.get('stationType') || undefined;
  const [activeKey, setActiveKey] = useState<string>(); // Tab选择
  // const [selectBtn, setSelectBtn] = useState<DatePickerType>(); // 时间颗粒度

  const [query, setQuery] = useState<ChartsDataReq>();

  // tabs请求
  const { data: tabData, loading: tabDataLoading } = useRequest(
    async () => {
      const res = await apiV2StationSignboardNodeDetailIndicatorListPost({
        nodeType: Number(nodeType) as NodeType,
        nodeId: Number(nodeId),
      });
      return (res?.list ?? []).map(item => {
        return {
          key: item.id!.toString(),
          label: item.name,
        };
      });
    },
    {
      ready: !!nodeId && !!nodeType,
      refreshDeps: [nodeId, nodeType],
    }
  );

  // 指标配置值获取
  const { data: indicatorValueData, loading: indicatorValueLoading } = useRequest(
    () => {
      return apiV2StationSignboardNodeDetailIndicatorValuePost({
        nodeType: Number(nodeType) as NodeType,
        nodeId: Number(nodeId),
        startDate: query?.startDate,
        endDate: query?.endDate,
        granularityType: query?.granularityType,
        indicatorId: query?.indicatorId,
        stationId: url_stationId ? Number(url_stationId) : undefined,
        stationType: url_stationType ? Number(url_stationType) : undefined,
      });
    },
    {
      ready: !!nodeId && !!nodeType && !!query,
      refreshDeps: [nodeId, nodeType, query],
    }
  );

  // 图表请求
  const { data: chartData, loading } = useRequest(
    () => {
      return apiV2StationSignboardNodeDetailIndicatorChartValuePost({
        nodeType: Number(nodeType) as NodeType,
        nodeId: Number(nodeId),
        startDate: query?.startDate,
        endDate: query?.endDate,
        granularityType: query?.granularityType,
        indicatorId: query?.indicatorId,
        stationId: url_stationId ? Number(url_stationId) : undefined,
        stationType: url_stationType ? Number(url_stationType) : undefined,
      });
    },
    {
      ready: !!nodeId && !!nodeType && !!query,
      refreshDeps: [nodeId, nodeType, query],
    }
  );

  const [currentItem, setCurrentItem] = useState<SingleCurrentItemType>(); // 保存每一个选择的信息
  // 默认选择第一个指标
  useEffect(() => {
    if (tabData && tabData.length) {
      setActiveKey(tabData[0].key);
    }
  }, [tabData]);

  useEffect(() => {
    if (activeKey) {
      const target = SingleIndicatorBtns()?.find(item => item.id.toString() === activeKey);
      const defaultAggrby = target?.defaultSelectBtn?.aggrby;

      setCurrentItem(target);
      setQuery({
        ...query,
        indicatorId: Number(activeKey),
        granularityType: defaultAggrby,
        startDate: getDefaultTime(defaultAggrby, dayjs()).from.format('YYYY-MM-DD'),
        endDate: getDefaultTime(defaultAggrby, dayjs()).to.format('YYYY-MM-DD'),
      });
    }
  }, [activeKey]);

  const onChangeRangePicker = (range: [Dayjs | null, Dayjs | null] | null) => {
    if (range) {
      const [beginDate, endDate] = range;
      if (!isNil(beginDate) && !isNil(endDate)) {
        // 查询的state
        setQuery({
          ...query,
          startDate: beginDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
        });
      }
    }
  };

  // 禁用时间范围
  const disabledDate: DatePickerProps['disabledDate'] = (current, { from }) => {
    if (current > dayjs().endOf('day')) return true;
    const mostDays =
      query?.granularityType === DatePickerType.DAY ? 31 : query?.granularityType === DatePickerType.HOUR ? 7 : 7;
    let timeType: 'days' | 'M' = 'days';
    if (query?.granularityType === DatePickerType.DAY || query?.granularityType === DatePickerType.HOUR) {
      timeType = 'days';
    }
    if (query?.granularityType === DatePickerType.MONTH) {
      timeType = 'M' as const;
    }
    let rangeGap = timeType === 'days' ? mostDays : 12;

    if (from) {
      return Math.abs(current.diff(from, timeType)) >= rangeGap;
    }
    return false;
  };

  // 查询图表
  const chartOption = useMemo(
    () =>
      Object.assign(
        {},
        currentItem?.getChartOption?.({
          chartData,
          query,
        })
      ),
    [chartData]
  );

  // 日期切换
  const handleGranularityType = (e: RadioChangeEvent) => {
    const selectGranularityType = e.target.value;
    const defaultStartDate = getDefaultTime(selectGranularityType, dayjs()).from;
    const defaultEndDate = getDefaultTime(selectGranularityType, dayjs()).to;
    setQuery({
      ...query,
      granularityType: selectGranularityType,
      startDate: defaultStartDate.format('YYYY-MM-DD'),
      endDate: defaultEndDate.format('YYYY-MM-DD'),
    });
  };

  const renderBorder = (name?: string) => {
    if (name?.includes('A相')) {
      return '#FFCB47';
    } else if (name?.includes('B相')) {
      return '#59D744';
    } else if (name?.includes('C相')) {
      return '#FF4D4F';
    } else {
      return undefined;
    }
  };

  return (
    <>
      <Spin spinning={tabDataLoading}>
        <Tabs onChange={v => setActiveKey(v)} activeKey={query?.indicatorId?.toString()} items={tabData} />
      </Spin>
      {query?.indicatorId && (
        <>
          <Radio.Group
            onChange={handleGranularityType}
            value={query?.granularityType}
            buttonStyle="solid"
            style={{ marginRight: 8 }}
          >
            {(currentItem?.dateBtn ?? []).map(btn => (
              <Radio.Button key={btn.aggrby} value={btn.aggrby}>
                {datePickerTypeDisplay[btn.aggrby as DatePickerType]}
              </Radio.Button>
            ))}
          </Radio.Group>
          <RangePicker
            value={[dayjs(query.startDate), dayjs(query.endDate)]}
            onChange={onChangeRangePicker}
            picker={query?.granularityType === DatePickerType.MONTH ? 'month' : 'date'}
            disabledDate={disabledDate}
            allowClear={false}
          />
          <Spin spinning={indicatorValueLoading}>
            <div className={styles.indicatorValue_card_wrapper}>
              {indicatorValueData?.list?.map(item => (
                <div
                  className={styles.itemcard_wrapper}
                  style={{ borderTop: `2px solid ${renderBorder(item.subName)}` }}
                  key={Math.random()}
                >
                  <Tooltip title={`${item?.subName ?? '--'}(${item?.physicalUnitName ?? '--'})`}>
                    <span className={styles.itemcard_wrapper_top_label}>{`${item?.subName ?? '--'}(${
                      item?.physicalUnitName ?? '--'
                    })`}</span>
                  </Tooltip>
                  <Tooltip title={!isNil(item?.value) ? item?.value.toFixed(2) : '--'}>
                    <span className={styles.itemcard_wrapper_top_value}>
                      {!isNil(item?.value) ? item?.value.toFixed(2) : '--'}
                    </span>
                  </Tooltip>
                </div>
              ))}
            </div>
          </Spin>

          <div className={styles.chart_box_inner}>
            <Charts loading={loading} chartData={chartData} option={chartOption} />
          </div>
        </>
      )}
    </>
  );
};

export default SingleDeviceContent;
